import { useState } from 'react'
import { cn } from '../../lib/cn'
import { holeIn } from '../../lib/holeProjection'
import { HolePlan } from './HolePlan'
import { HoleTraceStage } from './HoleTraceStage'
import type { HoleTraceStageProps } from './HoleTraceStage'
import { holeControlLabel } from './holeMedia'
import { useRovingRadioGroup } from './useRovingRadioGroup'

/** The stage's own props, minus the two the gallery spends on itself. */
export type HoleTraceGalleryProps = Omit<HoleTraceStageProps, 'showRail' | 'hole' | 'onHoleChange'>

/** A mini plan is a thumbnail, not a diagram: the shape of the hole and the walk. */
const MINI_WIDTH = 120
const MINI_HEIGHT = 150

/**
 * Every hole at once, as a wall of small plans, with the chosen one enlarged
 * on a stage beneath.
 *
 * Built and tested but NOT wired into the round page — the maintainer chose the
 * stage as the default and asked for all three presentations to exist as real
 * components so they can be tried later (decision record §2, 2026-08-22 —
 * `../../../../../../docs/fireside/club-web-decisions.md`).
 * Swapping it in is a one-line change in `RoundDetailScreen`'s section table.
 *
 * ## It delegates every honest refusal to the stage
 *
 * The scores-only plate and the untraced line are the STAGE's, rendered once
 * by the embedded stage below rather than restated here — two components
 * carrying the same copy is how the two sentences drift apart. The grid simply
 * does not appear where there is nothing to draw, and the stage says why.
 *
 * ## The grid IS the rail — including to a keyboard and a screen reader
 *
 * So the embedded stage hides its own (`showRail={false}`) and takes its hole
 * as a CONTROLLED prop. Nothing is remounted when a thumbnail is clicked,
 * which is what keeps the player toggles set as the member left them.
 *
 * And the grid carries the rail's whole CONTRACT rather than a resemblance to
 * it: `role="radiogroup"` labelled "Choose a hole", each thumbnail a
 * `role="radio"` with `aria-checked`, ONE tab stop roving with the chosen hole,
 * and the arrows, Home and End moving through it (`useRovingRadioGroup`, shared
 * with the stage). It emits `data-hole-chip` for the same reason: the
 * presentations are meant to be swapped for one another, and a swap that
 * silently costs a member their keyboard — or a test its handle — is not the
 * one-line change this component advertises.
 */
export function HoleTraceGallery({
  holeNumbers,
  courseHoles,
  players,
  geometry,
  courseName,
  initialHole = null,
  mediaCounts,
  className,
}: HoleTraceGalleryProps) {
  const opening =
    initialHole !== null && holeNumbers.includes(initialHole) ? initialHole : (holeNumbers[0] ?? 1)
  const [chosen, setChosen] = useState(opening)
  const hole = holeNumbers.includes(chosen) ? chosen : opening

  // ONE list, built once, of exactly the thumbnails that render — each hole
  // number paired with the geometry it is drawn from. The roving radio group
  // is sized from this same list, so an index can never point at a hole the
  // grid skipped: a guard inside the map that returned null for a missing
  // geometry would punch a hole in the index range and strand the arrow keys.
  const thumbnails = holeNumbers.flatMap((number) => {
    const holeGeometry = holeIn(geometry, number)
    return holeGeometry ? [{ number, holeGeometry }] : []
  })
  const anyShots = players.some((player) => player.shots.length > 0)

  const grid = useRovingRadioGroup(thumbnails.length, (index) => {
    const thumbnail = thumbnails[index]
    if (thumbnail) setChosen(thumbnail.number)
  })

  // The hole on stage need not be IN the grid: an untraced hole among traced
  // ones is on stage with its honest line and has no thumbnail to check. The
  // group still has to be reachable by Tab, so the first thumbnail keeps the
  // one tab stop when nothing is checked — the W3C rule for a radio group with
  // no chosen radio.
  const checkedIndex = thumbnails.findIndex((thumbnail) => thumbnail.number === hole)
  const tabStop = checkedIndex === -1 ? 0 : checkedIndex

  return (
    <div className={cn('flex flex-col gap-6', className)} data-hole-trace-gallery={hole}>
      {anyShots && thumbnails.length > 0 && geometry && (
        <div
          role="radiogroup"
          aria-label="Choose a hole"
          className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-2.5"
          data-gallery-grid=""
        >
          {thumbnails.map(({ number, holeGeometry }, index) => {
            const card = courseHoles.find((courseHole) => courseHole.number === number)
            const checked = number === hole
            const mediaCount = mediaCounts?.get(number) ?? 0
            return (
              <button
                key={number}
                ref={grid.optionRef(index)}
                type="button"
                role="radio"
                aria-checked={checked}
                aria-label={holeControlLabel(number, card, mediaCount)}
                tabIndex={index === tabStop ? 0 : -1}
                onClick={() => setChosen(number)}
                onKeyDown={(event) => grid.onKeyDown(event, index)}
                data-gallery-hole={number}
                data-hole-chip={number}
                data-media-count={mediaCount || undefined}
                className={cn(
                  'w-full rounded-club border bg-parchment p-1.5',
                  checked ? 'border-pine' : 'border-sage-soft',
                )}
              >
                <span className="mb-1 block text-[10.5px] font-semibold tabular-nums text-brass-deep">
                  {number}
                  {card && <span className="ml-1 font-normal text-ink-soft">par {card.par}</span>}
                  {mediaCount > 0 && (
                    <span className="ml-1 text-fairway" aria-hidden="true">●</span>
                  )}
                </span>
                {/* The thumbnail is decoration for the button that carries its
                    name — eighteen plans each announcing their own title and
                    description would read the whole round out twice. */}
                <span aria-hidden="true" className="block">
                  <HolePlan
                    hole={holeGeometry}
                    scale={geometry.scale}
                    par={card?.par ?? null}
                    players={players}
                    width={MINI_WIDTH}
                    height={MINI_HEIGHT}
                  />
                </span>
              </button>
            )
          })}
        </div>
      )}

      <HoleTraceStage
        holeNumbers={holeNumbers}
        courseHoles={courseHoles}
        players={players}
        geometry={geometry}
        courseName={courseName}
        hole={hole}
        onHoleChange={setChosen}
        showRail={false}
        mediaCounts={mediaCounts}
      />
    </div>
  )
}
